export const GAME_WIDTH = 480;
export const GAME_HEIGHT = 640;
export const HIGH_SCORE_KEY = "spaceShooterHighScore";

const PLAYER_SPEED = 300;
const PLAYER_SIZE = 28;
const BULLET_SPEED = 520;
const BULLET_WIDTH = 4;
const BULLET_HEIGHT = 12;
const FIRE_COOLDOWN = 0.2;
const ENEMY_SIZE = 26;
const ENEMY_POINTS = 10;
const ENEMY_BASE_SPEED = 90;
const ENEMY_EXTRA_SPEED = 120;
const ENEMY_SPEED_VARIANCE = 60;
const SPAWN_INTERVAL_MAX = 0.9;
const SPAWN_INTERVAL_MIN = 0.3;
const SECONDS_TO_MAX_DIFFICULTY = 60;
const STAR_LAYERS = [
	{ count: 60, speed: 20, size: 1, alpha: 0.4 },
	{ count: 35, speed: 50, size: 1.5, alpha: 0.7 },
	{ count: 15, speed: 110, size: 2, alpha: 1 },
];

export type Status = "ready" | "playing" | "gameover";

export interface Entity {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface Enemy extends Entity {
	speed: number;
}

export interface Star {
	x: number;
	y: number;
	speed: number;
	size: number;
	alpha: number;
}

export interface Input {
	left: boolean;
	right: boolean;
	up: boolean;
	down: boolean;
	fire: boolean;
}

export interface GameState {
	status: Status;
	player: Entity;
	bullets: Entity[];
	enemies: Enemy[];
	stars: Star[];
	score: number;
	highScore: number;
	fireCooldown: number;
	spawnTimer: number;
	elapsed: number;
}

export type Random = () => number;

export function createStars(random: Random = Math.random): Star[] {
	return STAR_LAYERS.flatMap((layer) =>
		Array.from({ length: layer.count }, () => ({
			x: random() * GAME_WIDTH,
			y: random() * GAME_HEIGHT,
			speed: layer.speed,
			size: layer.size,
			alpha: layer.alpha,
		}))
	);
}

export function createGame(
	highScore = 0,
	random: Random = Math.random,
	stars: Star[] = createStars(random)
): GameState {
	return {
		status: "ready",
		player: {
			x: GAME_WIDTH / 2 - PLAYER_SIZE / 2,
			y: GAME_HEIGHT - PLAYER_SIZE - 24,
			width: PLAYER_SIZE,
			height: PLAYER_SIZE,
		},
		bullets: [],
		enemies: [],
		stars,
		score: 0,
		highScore,
		fireCooldown: 0,
		spawnTimer: 1,
		elapsed: 0,
	};
}

export function overlaps(a: Entity, b: Entity): boolean {
	return (
		a.x < b.x + b.width &&
		a.x + a.width > b.x &&
		a.y < b.y + b.height &&
		a.y + a.height > b.y
	);
}

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, value));
}

function updateStars(stars: Star[], dt: number, random: Random): Star[] {
	return stars.map((star) => {
		const y = star.y + star.speed * dt;
		return y > GAME_HEIGHT
			? { ...star, y: y - GAME_HEIGHT, x: random() * GAME_WIDTH }
			: { ...star, y };
	});
}

export function step(
	state: GameState,
	input: Input,
	dt: number,
	random: Random = Math.random
): GameState {
	const stars = updateStars(state.stars, dt, random);

	if (state.status !== "playing") {
		return { ...state, stars };
	}

	const dx = (input.right ? 1 : 0) - (input.left ? 1 : 0);
	const dy = (input.down ? 1 : 0) - (input.up ? 1 : 0);
	const player = {
		...state.player,
		x: clamp(state.player.x + dx * PLAYER_SPEED * dt, 0, GAME_WIDTH - state.player.width),
		y: clamp(state.player.y + dy * PLAYER_SPEED * dt, 0, GAME_HEIGHT - state.player.height),
	};

	let fireCooldown = Math.max(0, state.fireCooldown - dt);
	let bullets = state.bullets
		.map((b) => ({ ...b, y: b.y - BULLET_SPEED * dt }))
		.filter((b) => b.y + b.height > 0);
	if (input.fire && fireCooldown === 0) {
		bullets.push({
			x: player.x + player.width / 2 - BULLET_WIDTH / 2,
			y: player.y - BULLET_HEIGHT,
			width: BULLET_WIDTH,
			height: BULLET_HEIGHT,
		});
		fireCooldown = FIRE_COOLDOWN;
	}

	const elapsed = state.elapsed + dt;
	let spawnTimer = state.spawnTimer - dt;
	let enemies = state.enemies
		.map((e) => ({ ...e, y: e.y + e.speed * dt }))
		.filter((e) => e.y < GAME_HEIGHT);
	if (spawnTimer <= 0) {
		// Spawn faster and with quicker enemies as time goes on.
		const difficulty = Math.min(elapsed / SECONDS_TO_MAX_DIFFICULTY, 1);
		enemies.push({
			x: random() * (GAME_WIDTH - ENEMY_SIZE),
			y: -ENEMY_SIZE,
			width: ENEMY_SIZE,
			height: ENEMY_SIZE,
			speed:
				ENEMY_BASE_SPEED +
				difficulty * ENEMY_EXTRA_SPEED +
				random() * ENEMY_SPEED_VARIANCE,
		});
		spawnTimer =
			SPAWN_INTERVAL_MAX - difficulty * (SPAWN_INTERVAL_MAX - SPAWN_INTERVAL_MIN);
	}

	let score = state.score;
	const hitBullets = new Set<Entity>();
	enemies = enemies.filter((enemy) => {
		const bullet = bullets.find((b) => !hitBullets.has(b) && overlaps(b, enemy));
		if (bullet) {
			hitBullets.add(bullet);
			score += ENEMY_POINTS;
			return false;
		}
		return true;
	});
	bullets = bullets.filter((b) => !hitBullets.has(b));

	const dead = enemies.some((enemy) => overlaps(enemy, player));

	return {
		...state,
		status: dead ? "gameover" : "playing",
		player,
		bullets,
		enemies,
		stars,
		score,
		highScore: Math.max(state.highScore, score),
		fireCooldown,
		spawnTimer,
		elapsed,
	};
}

export function loadHighScore(): number {
	try {
		const value = Number(window.localStorage.getItem(HIGH_SCORE_KEY));
		return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
	} catch {
		return 0;
	}
}

export function saveHighScore(score: number): void {
	try {
		window.localStorage.setItem(HIGH_SCORE_KEY, String(score));
	} catch {
		// Ignore storage errors (e.g. private browsing).
	}
}
